import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClipboardList, Plus, Sparkles, Trash2, Check, BarChart3 } from 'lucide-react';
import { AppShell } from '@/components/app/AppShell';
import { useCurrentUser } from '@/lib/auth';
import {
  useSurveys, useMySurveys, useSurveyResults, useCreateSurvey, useSetSurveyStatus,
  useRespondSurvey, useAnalyzeSurvey,
  type SurveyKind, type QuestionType, type MySurvey, type SurveyListItem, type SurveyTheme,
} from '@/lib/surveys';

const KIND_LABEL: Record<SurveyKind, string> = { pulse: 'Pulse', enps: 'eNPS', custom: 'Custom' };

export function SurveysPage() {
  const { t } = useTranslation();
  const me = useCurrentUser();
  const surveys = useSurveys();
  const mine = useMySurveys();
  const [creating, setCreating] = useState(false);
  const [resultsFor, setResultsFor] = useState<string | null>(null);
  const [respondTo, setRespondTo] = useState<MySurvey | null>(null);

  const isManager = ['owner', 'admin', 'manager'].includes(me.data?.role ?? '');
  const toRespond = (mine.data ?? []).filter((s) => !s.responded);

  return (
    <AppShell>
      <div className="px-8 py-8 max-w-5xl">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="display text-3xl font-semibold tracking-tight text-ink-900 serif-jp">{t('surveys.title', { defaultValue: 'Surveys' })}</h1>
            <p className="mt-1 text-sm text-ink-500">{t('surveys.subtitle', { defaultValue: 'Pulse, eNPS & custom questionnaires — with AI theme + sentiment rollups.' })}</p>
          </div>
          {isManager && (
            <button onClick={() => setCreating(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 transition">
              <Plus className="h-4 w-4" />{t('surveys.new', { defaultValue: 'New survey' })}
            </button>
          )}
        </div>

        {/* For you */}
        <section className="mt-6">
          <h2 className="text-sm font-semibold text-ink-700">{t('surveys.forYou', { defaultValue: 'For you' })}</h2>
          {toRespond.length === 0 && <p className="mt-2 text-sm text-ink-500">{t('surveys.noneForYou', { defaultValue: 'No open surveys awaiting your response.' })}</p>}
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {toRespond.map((s) => (
              <button key={s.id} onClick={() => setRespondTo(s)} className="text-start rounded-2xl border border-accent-300 bg-accent-50/50 p-4 hover:bg-accent-50 transition">
                <div className="flex items-center gap-2">
                  <ClipboardList className="h-4 w-4 text-accent-600" />
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-accent-700">{KIND_LABEL[s.kind]}</span>
                </div>
                <p className="mt-1 font-semibold text-ink-900">{s.title}</p>
                <p className="text-xs text-ink-500">{s.questions.length} {t('surveys.questions', { defaultValue: 'questions' })} · {s.anonymous ? t('surveys.anon', { defaultValue: 'anonymous' }) : t('surveys.attributed', { defaultValue: 'attributed' })}</p>
              </button>
            ))}
          </div>
        </section>

        {/* Manage */}
        {isManager && (
          <section className="mt-8">
            <h2 className="text-sm font-semibold text-ink-700">{t('surveys.manage', { defaultValue: 'All surveys' })}</h2>
            <div className="mt-3 rounded-2xl border border-ink-200 bg-white overflow-hidden">
              {surveys.isLoading && <p className="p-4 text-sm text-ink-500">{t('common.loading')}</p>}
              {surveys.data?.length === 0 && <p className="p-4 text-sm text-ink-500">{t('surveys.empty', { defaultValue: 'No surveys yet — create your first.' })}</p>}
              <ul className="divide-y divide-ink-100">
                {surveys.data?.map((s) => (
                  <SurveyRow key={s.id} survey={s} onResults={() => setResultsFor(resultsFor === s.id ? null : s.id)} expanded={resultsFor === s.id} />
                ))}
              </ul>
            </div>
          </section>
        )}
      </div>

      {creating && <CreateDialog onClose={() => setCreating(false)} />}
      {respondTo && <RespondDialog survey={respondTo} onClose={() => setRespondTo(null)} />}
    </AppShell>
  );
}

function SurveyRow({ survey: s, onResults, expanded }: { survey: SurveyListItem; onResults: () => void; expanded: boolean }) {
  const { t } = useTranslation();
  const setStatus = useSetSurveyStatus();
  const statusCls = s.status === 'open' ? 'bg-emerald-50 text-emerald-700' : s.status === 'closed' ? 'bg-ink-100 text-ink-500' : 'bg-blue-50 text-blue-700';
  return (
    <li>
      <div className="px-4 py-3 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[12rem]">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-400">{KIND_LABEL[s.kind]}</span>
            <span className={`text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 ${statusCls}`}>{t(`surveys.status_${s.status}`, { defaultValue: s.status })}</span>
          </div>
          <p className="font-semibold text-ink-900">{s.title}</p>
          <p className="num text-xs text-ink-500">{s.respondedCount}/{s.recipientCount} {t('surveys.responded', { defaultValue: 'responded' })}{s.recipientCount > 0 && ` · ${Math.round((s.respondedCount / s.recipientCount) * 100)}%`}</p>
        </div>
        {s.status === 'draft' && <button onClick={() => setStatus.mutate({ id: s.id, action: 'open' })} disabled={setStatus.isPending} className="text-xs font-semibold text-emerald-700 hover:text-emerald-800">{t('surveys.open', { defaultValue: 'Open' })}</button>}
        {s.status === 'open' && <button onClick={() => setStatus.mutate({ id: s.id, action: 'close' })} disabled={setStatus.isPending} className="text-xs font-semibold text-ink-600 hover:text-ink-800">{t('surveys.close', { defaultValue: 'Close' })}</button>}
        <button onClick={onResults} className="inline-flex items-center gap-1 text-xs font-semibold text-accent-700 hover:text-accent-600">
          <BarChart3 className="h-3.5 w-3.5" />{t('surveys.results', { defaultValue: 'Results' })}
        </button>
      </div>
      {expanded && <ResultsPanel surveyId={s.id} />}
    </li>
  );
}

function ResultsPanel({ surveyId }: { surveyId: string }) {
  const { t } = useTranslation();
  const results = useSurveyResults(surveyId);
  const analyze = useAnalyzeSurvey();
  const [err, setErr] = useState<string | null>(null);

  if (results.isLoading || !results.data) return <div className="px-4 pb-4 text-sm text-ink-500">{t('common.loading')}</div>;
  const r = results.data;
  const hasText = r.questions.some((q) => q.qtype === 'text' && q.count > 0);

  async function runAnalyze() {
    setErr(null);
    try { await analyze.mutateAsync(surveyId); } catch (e: unknown) {
      setErr(((e as { response?: { data?: { message?: string } } })?.response?.data?.message) || (e as Error).message);
    }
  }

  return (
    <div className="px-4 pb-4 bg-ink-50/40 border-t border-ink-100">
      <p className="num text-xs text-ink-500 pt-3">{t('surveys.responseRate', { defaultValue: 'Response rate' })}: <span className="font-semibold text-ink-800">{r.responseRate}%</span> ({r.respondedCount}/{r.recipientCount})</p>
      <div className="mt-3 space-y-3">
        {r.questions.map((q) => (
          <div key={q.id} className="rounded-lg border border-ink-100 bg-white p-3">
            <p className="text-sm font-medium text-ink-800">{q.prompt}</p>
            {q.qtype === 'nps' && (
              <p className="num mt-1 text-sm text-ink-600">eNPS <span className={`font-bold ${(q.enps ?? 0) >= 0 ? 'text-emerald-600' : 'text-warn-600'}`}>{q.enps ?? '—'}</span> · {t('surveys.avg', { defaultValue: 'avg' })} {q.average ?? '—'}/10 · {q.count} {t('surveys.responses', { defaultValue: 'responses' })}</p>
            )}
            {q.qtype === 'scale' && (
              <div className="mt-2">
                <p className="num text-sm text-ink-600">{t('surveys.avg', { defaultValue: 'avg' })} <span className="font-bold text-ink-900">{q.average ?? '—'}</span>/5 · {q.count} {t('surveys.responses', { defaultValue: 'responses' })}</p>
                <div className="mt-1 flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => {
                    const c = q.distribution?.[n] ?? 0;
                    const pct = q.count ? Math.round((c / q.count) * 100) : 0;
                    return <div key={n} className="flex-1 text-center"><div className="h-12 bg-ink-100 rounded relative overflow-hidden"><div className="absolute bottom-0 inset-x-0 bg-accent-400" style={{ height: `${pct}%` }} /></div><span className="text-[10px] text-ink-400">{n}</span></div>;
                  })}
                </div>
              </div>
            )}
            {q.qtype === 'text' && (
              <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                <p className="text-xs text-ink-400">{q.count} {t('surveys.responses', { defaultValue: 'responses' })}</p>
                {(q.texts ?? []).slice(0, 8).map((tx, i) => <p key={i} className="text-xs text-ink-600 border-s-2 border-ink-200 ps-2">{tx}</p>)}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* AI themes */}
      {hasText && (
        <div className="mt-4">
          <div className="flex items-center justify-between gap-2">
            <h4 className="text-xs font-semibold text-ink-700 flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-accent-600" />{t('surveys.aiThemes', { defaultValue: 'AI theme & sentiment rollup' })}</h4>
            <button onClick={runAnalyze} disabled={analyze.isPending} className="text-xs font-semibold text-accent-700 hover:text-accent-600 disabled:opacity-50">
              {analyze.isPending ? t('surveys.analyzing', { defaultValue: 'Analyzing…' }) : r.themes.length ? t('surveys.reanalyze', { defaultValue: 'Re-run' }) : t('surveys.analyze', { defaultValue: 'Analyze free-text' })}
            </button>
          </div>
          {err && <p className="mt-1 text-xs text-warn-600">{err}</p>}
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            {r.themes.map((th, i) => <ThemeCard key={i} theme={th} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function ThemeCard({ theme }: { theme: SurveyTheme }) {
  const cls = theme.sentiment === 'positive' ? 'border-emerald-200 bg-emerald-50' : theme.sentiment === 'negative' ? 'border-warn-500/30 bg-warn-50' : 'border-ink-200 bg-white';
  const dot = theme.sentiment === 'positive' ? 'bg-emerald-500' : theme.sentiment === 'negative' ? 'bg-warn-500' : 'bg-ink-400';
  return (
    <div className={`rounded-lg border p-3 ${cls}`}>
      <div className="flex items-center gap-2">
        <span className={`h-2 w-2 rounded-full ${dot}`} />
        <p className="text-sm font-semibold text-ink-900 flex-1">{theme.theme}</p>
        <span className="num text-[10px] text-ink-500">×{theme.mentionCount}</span>
      </div>
      {theme.summary && <p className="mt-1 text-xs text-ink-600">{theme.summary}</p>}
    </div>
  );
}

// ───────────────────────── Dialogs ─────────────────────────

function Modal({ onClose, title, children, wide }: { onClose: () => void; title: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className="fixed inset-0 z-50 bg-ink-950/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className={`bg-white rounded-2xl shadow-glow border border-ink-200 w-full p-6 max-h-[85vh] overflow-y-auto ${wide ? 'max-w-2xl' : 'max-w-lg'}`}>
        <h2 className="text-lg font-semibold text-ink-900">{title}</h2>
        <div className="mt-4">{children}</div>
      </div>
    </div>
  );
}

function CreateDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const create = useCreateSurvey();
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState<SurveyKind>('pulse');
  const [anonymous, setAnonymous] = useState(true);
  const [questions, setQuestions] = useState<Array<{ prompt: string; qtype: QuestionType }>>([{ prompt: '', qtype: 'scale' }]);
  const [error, setError] = useState<string | null>(null);

  const needsQuestions = kind !== 'enps';

  async function submit() {
    setError(null);
    try {
      const qs = questions.map((q) => ({ ...q, prompt: q.prompt.trim() })).filter((q) => q.prompt);
      await create.mutateAsync({ title, kind, anonymous, questions: needsQuestions ? qs : undefined });
      onClose();
    } catch (e: unknown) {
      setError(((e as { response?: { data?: { message?: string } } })?.response?.data?.message) || (e as Error).message);
    }
  }

  return (
    <Modal onClose={onClose} title={t('surveys.createTitle', { defaultValue: 'New survey' })} wide>
      <label className="block">
        <span className="block text-xs font-medium text-ink-700 mb-1">{t('surveys.titleLabel', { defaultValue: 'Title' })}</span>
        <input value={title} onChange={(e) => setTitle(e.target.value)} className="input" placeholder={t('surveys.titlePlaceholder', { defaultValue: 'Q2 engagement pulse' })} />
      </label>
      <div className="mt-4 grid grid-cols-2 gap-3">
        <label className="block">
          <span className="block text-xs font-medium text-ink-700 mb-1">{t('surveys.kindLabel', { defaultValue: 'Type' })}</span>
          <select value={kind} onChange={(e) => setKind(e.target.value as SurveyKind)} className="input">
            <option value="pulse">{t('surveys.kind_pulse', { defaultValue: 'Pulse (custom scale)' })}</option>
            <option value="enps">{t('surveys.kind_enps', { defaultValue: 'eNPS (auto questions)' })}</option>
            <option value="custom">{t('surveys.kind_custom', { defaultValue: 'Custom' })}</option>
          </select>
        </label>
        <label className="flex items-end gap-2 pb-2">
          <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} className="accent-accent-500" />
          <span className="text-sm text-ink-700">{t('surveys.anonymous', { defaultValue: 'Anonymous responses' })}</span>
        </label>
      </div>

      {needsQuestions ? (
        <div className="mt-4">
          <span className="block text-xs font-medium text-ink-700 mb-1">{t('surveys.questionsLabel', { defaultValue: 'Questions' })}</span>
          <div className="space-y-2">
            {questions.map((q, i) => (
              <div key={i} className="flex gap-2">
                <input value={q.prompt} onChange={(e) => setQuestions(questions.map((x, j) => j === i ? { ...x, prompt: e.target.value } : x))} className="input flex-1" placeholder={t('surveys.questionPlaceholder', { defaultValue: 'e.g. I feel supported by my manager' })} />
                <select value={q.qtype} onChange={(e) => setQuestions(questions.map((x, j) => j === i ? { ...x, qtype: e.target.value as QuestionType } : x))} className="input w-28">
                  <option value="scale">{t('surveys.qtype_scale', { defaultValue: 'Scale 1–5' })}</option>
                  <option value="text">{t('surveys.qtype_text', { defaultValue: 'Free text' })}</option>
                  <option value="nps">{t('surveys.qtype_nps', { defaultValue: 'NPS 0–10' })}</option>
                </select>
                {questions.length > 1 && <button onClick={() => setQuestions(questions.filter((_, j) => j !== i))} className="text-ink-400 hover:text-warn-600"><Trash2 className="h-4 w-4" /></button>}
              </div>
            ))}
          </div>
          <button onClick={() => setQuestions([...questions, { prompt: '', qtype: 'scale' }])} className="mt-2 text-xs font-semibold text-accent-700 hover:text-accent-600">+ {t('surveys.addQuestion', { defaultValue: 'Add question' })}</button>
        </div>
      ) : (
        <p className="mt-4 text-xs text-ink-500 rounded-lg bg-ink-50 px-3 py-2">{t('surveys.enpsNote', { defaultValue: 'eNPS adds the standard 0–10 recommend question plus a "why" free-text follow-up automatically.' })}</p>
      )}

      {error && <div className="mt-4 rounded-md bg-warn-50 border border-warn-500/30 text-warn-600 text-sm px-3 py-2">{error}</div>}
      <div className="flex justify-end gap-2 pt-5">
        <button onClick={onClose} className="px-3 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-100 rounded-lg">{t('common.cancel')}</button>
        <button onClick={submit} disabled={create.isPending || title.trim().length < 2} className="px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 disabled:opacity-50">{create.isPending ? t('common.saving', { defaultValue: 'Saving…' }) : t('surveys.createBtn', { defaultValue: 'Create' })}</button>
      </div>
    </Modal>
  );
}

function RespondDialog({ survey, onClose }: { survey: MySurvey; onClose: () => void }) {
  const { t } = useTranslation();
  const respond = useRespondSurvey();
  const [answers, setAnswers] = useState<Record<string, { score?: number; answerText?: string }>>({});
  const [error, setError] = useState<string | null>(null);

  function set(qid: string, patch: { score?: number; answerText?: string }) {
    setAnswers({ ...answers, [qid]: { ...answers[qid], ...patch } });
  }

  async function submit() {
    setError(null);
    try {
      const payload = survey.questions.map((q) => ({ questionId: q.id, ...answers[q.id] }));
      await respond.mutateAsync({ id: survey.id, answers: payload });
      onClose();
    } catch (e: unknown) {
      setError(((e as { response?: { data?: { message?: string } } })?.response?.data?.message) || (e as Error).message);
    }
  }

  return (
    <Modal onClose={onClose} title={survey.title}>
      {survey.anonymous && <p className="text-[11px] text-ink-500 mb-3 flex items-center gap-1"><Check className="h-3 w-3 text-emerald-500" />{t('surveys.anonNote', { defaultValue: 'Your answers are recorded anonymously.' })}</p>}
      <div className="space-y-4">
        {survey.questions.map((q) => (
          <div key={q.id}>
            <p className="text-sm font-medium text-ink-800 mb-1.5">{q.prompt}</p>
            {q.qtype === 'text' && (
              <textarea value={answers[q.id]?.answerText ?? ''} onChange={(e) => set(q.id, { answerText: e.target.value })} rows={2} className="input w-full" />
            )}
            {q.qtype === 'scale' && (
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} onClick={() => set(q.id, { score: n })} className={`h-10 w-10 rounded-lg border text-sm font-semibold transition ${answers[q.id]?.score === n ? 'bg-accent-500 text-white border-accent-500' : 'border-ink-300 text-ink-700 hover:bg-ink-50'}`}>{n}</button>
                ))}
              </div>
            )}
            {q.qtype === 'nps' && (
              <div className="flex flex-wrap gap-1">
                {Array.from({ length: 11 }, (_, n) => (
                  <button key={n} onClick={() => set(q.id, { score: n })} className={`h-9 w-9 rounded-lg border text-xs font-semibold transition ${answers[q.id]?.score === n ? 'bg-accent-500 text-white border-accent-500' : 'border-ink-300 text-ink-700 hover:bg-ink-50'}`}>{n}</button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      {error && <div className="mt-4 rounded-md bg-warn-50 border border-warn-500/30 text-warn-600 text-sm px-3 py-2">{error}</div>}
      <div className="flex justify-end gap-2 pt-5">
        <button onClick={onClose} className="px-3 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-100 rounded-lg">{t('common.cancel')}</button>
        <button onClick={submit} disabled={respond.isPending} className="px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 disabled:opacity-50">{respond.isPending ? t('common.saving', { defaultValue: 'Saving…' }) : t('surveys.submitResponse', { defaultValue: 'Submit' })}</button>
      </div>
    </Modal>
  );
}
